import { QuestionModel } from "../QuestionModel";
import { getSettingString, hasSetting } from "./settingUtilities";

/** Values for `CarrouselChoiceListAnswerType` as stored in question settings (survey JSON). */
export const CARROUSEL_ANSWER_TYPE_RADIO = "RadioButton";
export const CARROUSEL_ANSWER_TYPE_CHECKBOX = "CheckBox";
export const CARROUSEL_ANSWER_TYPE_SCALE = "Scale";

const DEFAULT_CARROUSEL_ANSWER_TYPE = CARROUSEL_ANSWER_TYPE_RADIO;

export function getCarrouselChoiceListAnswerType(question: QuestionModel): string {
    if (hasSetting(question, "CarrouselChoiceListAnswerType")) {
        return (
            getSettingString(question, "CarrouselChoiceListAnswerType") ||
            DEFAULT_CARROUSEL_ANSWER_TYPE
        );
    }
    return DEFAULT_CARROUSEL_ANSWER_TYPE;
}

export function isCarrouselCheckboxMode(question: QuestionModel): boolean {
    return getCarrouselChoiceListAnswerType(question) === CARROUSEL_ANSWER_TYPE_CHECKBOX;
}

export function isCarrouselScaleMode(question: QuestionModel): boolean {
    return getCarrouselChoiceListAnswerType(question) === CARROUSEL_ANSWER_TYPE_SCALE;
}

export function isCarrouselRadioLikeMode(question: QuestionModel): boolean {
    const answerType = getCarrouselChoiceListAnswerType(question);
    return (
        answerType === CARROUSEL_ANSWER_TYPE_RADIO || answerType === CARROUSEL_ANSWER_TYPE_SCALE
    );
}

/**
 * Physical `translateX` offset (px) that centers the active slide inside the statements
 * strip. `flex-direction: row` auto-reverses visual item order under `dir="rtl"` (item 0
 * renders rightmost instead of leftmost), which mirrors the layout this offset targets —
 * so the LTR value is negated for RTL rather than recomputed.
 */
export function computeCarrouselStatementTransform(
    totalItemWidth: number,
    itemWidth: number,
    gap: number,
    activeIndex: number,
    isRtl: boolean,
): number {
    const ltrTransformValue = totalItemWidth / 2 - itemWidth / 2 + gap - (itemWidth + gap) * activeIndex;
    return isRtl ? -ltrTransformValue : ltrTransformValue;
}

/** Direct children of a scale choice list that represent selectable answers. */
export function getCarrouselScaleChoiceElements(container: HTMLElement): HTMLElement[] {
    const scaleItems = Array.from(
        container.querySelectorAll<HTMLElement>(":scope > .carrousel-scale__item"),
    );
    if (scaleItems.length > 0) {
        return scaleItems;
    }
    return Array.from(
        container.querySelectorAll<HTMLElement>(
            ':scope > .choice, :scope > [role="radio"], :scope > [role="checkbox"]',
        ),
    );
}

/**
 * True when rendered scale choices have wrapped to a second row in the DOM.
 * Used as a safety net after applying a content-based horizontal layout plan.
 */
export function carrouselScaleChoicesDomWrapped(container: HTMLElement): boolean {
    const items = getCarrouselScaleChoiceElements(container);
    if (items.length < 2) {
        return false;
    }
    return carrouselScaleChoicesWrappedToNextRow(items);
}

/** Extra px subtracted from row width when testing unwrap from stacked layout. */
export const CARROUSEL_SCALE_HORIZONTAL_WRAP_BUFFER_PX = 8;

function carrouselScaleChoicesWrappedToNextRow(items: HTMLElement[]): boolean {
    const firstTop = items[0].offsetTop;
    return items.some((item, index) => index > 0 && item.offsetTop > firstTop + 1);
}

